import { apiBaseUrl } from "./auth";
import { getStoredLanguage } from "../context/I18nContext";

const LESSONS_BASE = `${apiBaseUrl}/api/lessons`;

async function request(
  path,
  { accessToken, method = "GET", body, params } = {},
) {
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    "Accept-Language": getStoredLanguage(),
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const queryParams = params
    ? { ...params, lang: getStoredLanguage() }
    : { lang: getStoredLanguage() };
  const queryString = `?${new URLSearchParams(queryParams).toString()}`;

  const response = await fetch(`${LESSONS_BASE}${path}${queryString}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const isJson = response.headers
    .get("content-type")
    ?.includes("application/json");
  const payload = isJson ? await response.json() : {};

  if (!response.ok) {
    const message = payload.error || "Une erreur est survenue";
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  return payload;
}

export function fetchHomeworks({ accessToken, params } = {}) {
  return request("/homeworks/", { accessToken, params });
}

export function fetchHomework({ accessToken, homeworkId, params } = {}) {
  if (!homeworkId) throw new Error("homeworkId is required");
  return request(`/homeworks/${homeworkId}/`, { accessToken, params });
}

export function createHomework({ accessToken, payload }) {
  return request("/homeworks/", {
    accessToken,
    method: "POST",
    body: payload,
  });
}

export function updateHomework({ accessToken, homeworkId, payload }) {
  if (!homeworkId) throw new Error("homeworkId is required");
  return request(`/homeworks/${homeworkId}/`, {
    accessToken,
    method: "PATCH",
    body: payload,
  });
}

export function deleteHomework({ accessToken, homeworkId }) {
  if (!homeworkId) throw new Error("homeworkId is required");
  return request(`/homeworks/${homeworkId}/`, {
    accessToken,
    method: "DELETE",
  });
}

export function createHomeworkExercise({ accessToken, homeworkId, payload }) {
  if (!homeworkId) throw new Error("homeworkId is required");
  return request(`/homeworks/${homeworkId}/exercises/`, {
    accessToken,
    method: "POST",
    body: payload,
  });
}

export function updateHomeworkExercise({ accessToken, exerciseId, payload }) {
  if (!exerciseId) throw new Error("exerciseId is required");
  return request(`/homework-exercises/${exerciseId}/`, {
    accessToken,
    method: "PATCH",
    body: payload,
  });
}

export function deleteHomeworkExercise({ accessToken, exerciseId }) {
  if (!exerciseId) throw new Error("exerciseId is required");
  return request(`/homework-exercises/${exerciseId}/`, {
    accessToken,
    method: "DELETE",
  });
}

export function submitHomework({ accessToken, homeworkId, payload }) {
  if (!homeworkId) throw new Error("homeworkId is required");
  return request(`/homeworks/${homeworkId}/submit/`, {
    accessToken,
    method: "POST",
    body: payload,
  });
}

export function fetchHomeworkSubmissions({ accessToken, homeworkId }) {
  if (!homeworkId) throw new Error("homeworkId is required");
  return request(`/homeworks/${homeworkId}/submissions/`, {
    accessToken,
    method: "GET",
  });
}

export function updateHomeworkSubmission({
  accessToken,
  submissionId,
  payload,
}) {
  if (!submissionId) throw new Error("submissionId is required");
  return request(`/homework-submissions/${submissionId}/`, {
    accessToken,
    method: "PATCH",
    body: payload,
  });
}
